import { motion, useReducedMotion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export const FITLAB_CONTACT = {
  email: 'fitlab.iqq@gmail.com',
  phone: '+56 9 5885 3328',
  phoneDigits: '56958853328',
} as const

type Channel = 'email' | 'whatsapp'

interface ContactButtonProps {
  channel?: Channel
  variant?: 'primary' | 'inverse' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  withIcon?: boolean
  className?: string
  children?: React.ReactNode
  message?: string
}

const VARIANT_CLASS: Record<NonNullable<ContactButtonProps['variant']>, string> = {
  primary:
    'bg-gradient-to-r from-fitlab-green to-fitlab-green-hover text-white shadow-lg shadow-fitlab-green/20 hover:shadow-xl hover:shadow-fitlab-green/40',
  inverse: 'bg-white text-fitlab-green-hover shadow-lg hover:bg-fitlab-neutral-50',
  ghost:
    'bg-transparent text-white border-2 border-white/70 hover:bg-white hover:text-fitlab-green-hover',
}

const SIZE_CLASS: Record<NonNullable<ContactButtonProps['size']>, string> = {
  sm: 'px-5 py-2 text-sm gap-1.5',
  md: 'px-7 py-3 text-base gap-2',
  lg: 'px-9 py-4 text-lg gap-2.5',
}

/**
 * Botón de contacto unificado FITLAB — abre mailto: o wa.me según `channel`.
 *
 * El mockup oficial muestra UN solo CTA verde (gradiente con flecha) replicado
 * en header, hero, sección final y footer. Reemplaza el formulario stub anterior
 * (eliminado por decisión del cliente — comunicación directa por email/WhatsApp).
 *
 * Spring hover + scale tap = micro-interacción Disney "anticipation".
 */
/** Mensaje WhatsApp por defecto — canal primario en Chile, evita depender de cliente mail. */
const DEFAULT_WA_MESSAGE =
  'Hola FITLAB, me gustaría más información sobre el servicio de salud ocupacional para mi empresa.'

export default function ContactButton({
  channel = 'whatsapp',
  variant = 'primary',
  size = 'md',
  withIcon = true,
  className,
  children = 'Contacto',
  message,
}: ContactButtonProps) {
  const reduced = useReducedMotion()

  const waMessage = message ?? DEFAULT_WA_MESSAGE
  const href =
    channel === 'whatsapp'
      ? `https://wa.me/${FITLAB_CONTACT.phoneDigits}?text=${encodeURIComponent(waMessage)}`
      : `mailto:${FITLAB_CONTACT.email}${message ? `?subject=${encodeURIComponent(message)}` : ''}`

  return (
    <motion.a
      href={href}
      target={channel === 'whatsapp' ? '_blank' : undefined}
      rel={channel === 'whatsapp' ? 'noopener noreferrer' : undefined}
      whileHover={reduced ? undefined : { y: -2, scale: 1.03 }}
      whileTap={reduced ? undefined : { scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 380, damping: 22 }}
      className={cn(
        'group relative inline-flex items-center justify-center rounded-full font-semibold transition-shadow duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fitlab-green focus-visible:ring-offset-2',
        VARIANT_CLASS[variant],
        SIZE_CLASS[size],
        className,
      )}
    >
      <span className="relative z-10">{children}</span>
      {withIcon && (
        <ArrowRight
          className="relative z-10 h-4 w-4 transition-transform group-hover:translate-x-0.5"
          strokeWidth={2.5}
          aria-hidden="true"
        />
      )}
    </motion.a>
  )
}
